"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sameItems = exports.stable = exports.deepEqual = void 0;
/**
 * Structural equality for poll results, so a refetch that returns the same
 * data does not hand React a new reference.
 *
 * Every aggregation hook polls AppSync every 30 s and used to call
 * `setState(new Map(...))` / `setState([...])` unconditionally. Same numbers,
 * new identity, so every consumer re-rendered: on the Pi kiosk that was a
 * ~1 s main-thread burst every 30 s (Tamagui restyling + i18n) while the
 * screen sat unchanged, and it landed mid-swipe often enough to show as
 * 300-500 ms frames. `setState((prev) => stable(prev, next))` returns the
 * previous reference when nothing changed, and React bails out of the render.
 *
 * Handles primitives, arrays, Maps, Sets, Dates and plain objects — the shapes
 * the hooks actually store. Anything else falls back to reference equality.
 */
function deepEqual(a, b) {
    if (a === b)
        return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
        // NaN !== NaN but should count as equal for readings.
        return typeof a === 'number' && typeof b === 'number' && Number.isNaN(a) && Number.isNaN(b);
    }
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    }
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++)
            if (!deepEqual(a[i], b[i]))
                return false;
        return true;
    }
    if (a instanceof Map || b instanceof Map) {
        if (!(a instanceof Map) || !(b instanceof Map) || a.size !== b.size)
            return false;
        for (const [k, v] of a)
            if (!b.has(k) || !deepEqual(v, b.get(k)))
                return false;
        return true;
    }
    if (a instanceof Set || b instanceof Set) {
        if (!(a instanceof Set) || !(b instanceof Set) || a.size !== b.size)
            return false;
        for (const v of a)
            if (!b.has(v))
                return false;
        return true;
    }
    const ao = a;
    const bo = b;
    const ak = Object.keys(ao);
    if (ak.length !== Object.keys(bo).length)
        return false;
    for (const k of ak) {
        if (!Object.prototype.hasOwnProperty.call(bo, k) || !deepEqual(ao[k], bo[k]))
            return false;
    }
    return true;
}
exports.deepEqual = deepEqual;
/** For `setState((prev) => stable(prev, next))`: keeps `prev` when nothing changed. */
function stable(prev, next) {
    return deepEqual(prev, next) ? prev : next;
}
exports.stable = stable;
/** Same objects in the same order (identity, not deep): cheap check for an unchanged subset. */
function sameItems(a, b) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
}
exports.sameItems = sameItems;
